import * as React from 'react';

import { useBoolean, useDebounce } from 'hooks';
import { fetcher } from 'services';
import { HeaderFiltersType } from 'context/GlobalFiltersContext/initialState';

import { ChartSeriesType, ChartsParamsType } from '../types';
import { ChartOccurrencesRequiredBySystemType } from './types';
import { LkpChart } from '../Chart';
import { getStackedChartOptions } from './options';
import { LkpChartTopFilter } from '../Filters';
import { getErrorMessageByStatusCode, groupBy } from 'utils';
import { isNumber } from 'fp-ts/lib/number';
import { useParams } from 'react-router-dom';

export interface LkpChartOccurrencesRequiredBySystemProps {
  headerFilters: HeaderFiltersType;
  httpPath?: string;
  title?: string;
}

const DEFAULT_SERIES = [
  { name: 'Elétrico', data: [] },
  { name: 'Civil', data: [] },
  { name: 'Mecânico', data: [] },
  { name: 'Climatização', data: [] },
  { name: 'Refrigeração', data: [] },
  { name: 'Mobiliário', data: [] },
  { name: 'Áudio e video', data: [] },
  { name: 'Hidráulico', data: [] },
  { name: 'Limpeza', data: [] },
  { name: 'CFTV', data: [] },
  { name: 'SDAI', data: [] },
];

export function LkpChartOccurrencesRequiredBySystem({
  headerFilters,
  httpPath = '',
  title,
}: LkpChartOccurrencesRequiredBySystemProps): React.ReactElement {
  const { value: isLoading, setValue: setLoading } = useBoolean();
  const { value: isError, setValue: setIsError } = useBoolean();
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const { dateFrom, dateTo } = useParams<{ dateFrom: string; dateTo: string }>();

  const [series, setSeries] = React.useState<Array<ChartSeriesType>>(DEFAULT_SERIES);
  const [period, setPeriod] = React.useState<Array<string>>([]);
  const [paramsSystems, setParamsSystems] = React.useState<string>('');
  const [params, setParams] = React.useState<ChartsParamsType>({
    Empresa: headerFilters?.companies?.id || 0,
    Sites: headerFilters?.units?.id,
    exibirMes: false,
    Inicio: dateFrom,
    Fim: dateTo,
    GrupoSite: headerFilters?.unitGroups?.id || [],
    SubGrupoSite: headerFilters?.unitSubgroups?.id || [],
    Cidade: headerFilters?.cities?.id || [],
    Estado: headerFilters?.states?.id || [],
  });

  const debouncedStartValue = useDebounce<string>(params?.Inicio || '', 500);
  const debouncedEnvValue = useDebounce<string>(params?.Fim || '', 500);
  const debouncedParamsSystemsValue = useDebounce<string>(paramsSystems || '', 500);

  React.useEffect(() => {
    (async () => {
      await fetchChartData();
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedStartValue,
    debouncedEnvValue,
    debouncedParamsSystemsValue,
    headerFilters?.unitGroups?.id,
    headerFilters?.unitSubgroups?.id,
    headerFilters?.companies?.id,
    headerFilters?.states?.id,
    headerFilters?.cities?.id,
  ]);

  const fetchChartData = async () => {
    setLoading(true);

    let _httpPath = httpPath;
    if (!!paramsSystems) _httpPath = `${_httpPath}?${paramsSystems}`;

    const result = await fetcher
      .get<Array<ChartOccurrencesRequiredBySystemType>>(_httpPath, 'dashboard', {
        params: {
          ...params,
          GrupoSite: headerFilters?.unitGroups?.id,
          SubGrupoSite: headerFilters?.unitSubgroups?.id,
          Cidade: headerFilters?.cities?.id,
          Estado: headerFilters?.states?.id,
        },
      })
      .catch(error => {
        const message = getErrorMessageByStatusCode(error, _httpPath);
        setErrorMessage(message);
        setIsError(true);
      });

    if (result) {
      const _groupSistema = groupBy<ChartOccurrencesRequiredBySystemType>(result, d => d.sistema);
      const _series = _groupSistema.map(({ name, data: d }) => {
        const data = d.map(({ quantidade }) => (isNumber(Number(quantidade)) ? Number(quantidade) : 0));
        return { name, data };
      });
      setSeries(_series);

      const _groupPeriodo = groupBy<ChartOccurrencesRequiredBySystemType>(result, d => d.periodo);
      setPeriod(_groupPeriodo.map(period => period?.name));
    }

    setLoading(false);
  };

  const handleChangeStartDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: Inicio } = event.target;
    setParams({ ...params, Inicio });
  };

  const handleChangeEndDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: Fim } = event.target;
    setParams({ ...params, Fim });
  };

  const handleSelectSystems = (selectedSystems: string) => {
    setParamsSystems(selectedSystems);
  };

  return (
    <LkpChart
      title={title}
      companyId={headerFilters?.companies?.id || 0}
      series={series}
      //width={1700}
      options={getStackedChartOptions(period, {
        legend: {
          position: 'right',
        },
        colors: [
          '#00C0C7',
          '#5144D3',
          '#E8871A',
          '#DA3490',
          '#9089FA',
          '#47E26F',
          '#2780EB',
          '#6F38B1',
          '#DFBF03',
          '#CB6F10',
          '#268D6C',
          '#9BEC54',
          '#3366CC',
          '#DC3912',
          '#FF9900',
          '#109618',
          '#990099',
          '#0099C6',
          '#DD4477',
          '#66AA00',
          '#B82E2E',
          '#316395',
          '#994499',
          '#22AA99',
          '#AAAA11',
          '#6633CC',
          '#E67300',
          '#8B0707',
          '#CCCCCC',
          '#651067',
          '#329262',
        ],
      })}
      loading={isLoading}
      error={isError}
      errorMessage={errorMessage}
      onChartTryAgain={fetchChartData}
      filter={
        <LkpChartTopFilter
          onChangeStartDate={handleChangeStartDate}
          onChangeEndDate={handleChangeEndDate}
          onSelectSystems={handleSelectSystems}
        />
      }
    />
  );
}
